import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, FileBox, ShieldAlert, ShieldCheck, Upload, X } from "lucide-react";
import { Btn } from "../../components/Btn";
import { useToast } from "../../components/Toast";
import { getMyApps, uploadApk } from "../../api/endpoints";
import { ApiError } from "../../api/types";

export default function UploadPage() {
  const navigate = useNavigate();
  const push = useToast();
  const { pkg: pkgParam } = useParams<{ pkg?: string }>();
  const [params] = useSearchParams();

  // Список приложений разработчика — для выпадайки и автоподстановки pkg.
  const myAppsQ = useQuery({
    queryKey: ["dev", "my-apps"],
    queryFn: getMyApps,
  });

  // pkg либо приходит из роута/квери, либо пользователь выбрал явно через select.
  // Если ни того ни другого — подставляем первое приложение из my-apps как дефолт.
  const lockedPkg = pkgParam || params.get("pkg") || "";
  const [pkgOverride, setPkgOverride] = useState<string>("");
  const pkg = pkgOverride || lockedPkg || myAppsQ.data?.[0]?.pkg || "";

  const currentApp = useMemo(
    () => (myAppsQ.data ?? []).find((a) => a.pkg === pkg),
    [myAppsQ.data, pkg],
  );

  const [file, setFile] = useState<File | null>(null);
  const [changelog, setChangelog] = useState<string>("");
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadMut = useMutation({
    mutationFn: () => {
      if (!file) throw new ApiError(0, "no_file", "Выберите APK файл");
      if (!pkg) throw new ApiError(0, "no_pkg", "Выберите приложение");
      return uploadApk(pkg, file, changelog, (loaded, total) =>
        setProgress({ loaded, total }),
      );
    },
    onSuccess: (ver) => {
      push({ kind: "success", text: `Версия ${ver.version} загружена и опубликована` });
      navigate(`/dev/apps/${encodeURIComponent(pkg)}/`);
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Ошибка загрузки") : "Ошибка загрузки";
      push({ kind: "warn", text: msg });
    },
    onSettled: () => {
      setProgress(null);
    },
  });

  function chooseFile(f: File | null) {
    if (!f) {
      setFile(null);
      return;
    }
    // Минимальная проверка: расширение и размер.
    if (!/\.apk$/i.test(f.name)) {
      push({ kind: "warn", text: "Ожидается файл с расширением .apk" });
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      push({ kind: "warn", text: "Файл больше 200 МБ — отклонён сервером" });
      return;
    }
    setFile(f);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    chooseFile(f);
  }

  function onPickClick() {
    fileInputRef.current?.click();
  }

  const pct = progress && progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;
  const sizeMb = file ? file.size / (1024 * 1024) : 0;

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Новая версия
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {pkg ? (
              <>
                <span className="font-mono">{pkg}</span>
                {currentApp?.current_version && <> · текущая v{currentApp.current_version}</>}
              </>
            ) : (
              "выберите приложение"
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Btn onClick={() => navigate("/dev")}>Отмена</Btn>
          <Btn
            variant="primary"
            icon={Check}
            onClick={() => uploadMut.mutate()}
          >
            {uploadMut.isPending ? "Загрузка…" : "Опубликовать"}
          </Btn>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          {/* Выбор приложения (если pkg не в роуте — даём селектор) */}
          {!lockedPkg && (
            <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Приложение</h2>
              <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">Куда заливаем новую версию</p>
              <select
                value={pkg}
                onChange={(e) => setPkgOverride(e.target.value)}
                className="mt-3 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
                disabled={uploadMut.isPending}
              >
                {!pkg && <option value="">— выберите —</option>}
                {(myAppsQ.data ?? []).map((a) => (
                  <option key={a.pkg} value={a.pkg}>
                    {a.pkg} · {a.name}
                  </option>
                ))}
              </select>
            </section>
          )}

          {/* APK file: dropzone + fallback input */}
          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Файл APK</h2>

            {!file && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={onPickClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPickClick();
                  }
                }}
                className={
                  "mt-3 grid cursor-pointer place-items-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors " +
                  (dragOver
                    ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5"
                    : "border-[var(--color-border-strong)] bg-[var(--color-surface-2)] hover:border-[var(--color-fg-muted)]")
                }
              >
                <Upload size={28} className="text-[var(--color-fg-faint)]" />
                <div className="mt-3 text-[13px] font-medium text-[var(--color-fg)]">
                  Перетащите APK или нажмите, чтобы выбрать
                </div>
                <div className="mt-1 text-[11.5px] text-[var(--color-fg-muted)]">
                  Поддерживается только формат APK. Размер до 200 МБ.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".apk,application/vnd.android.package-archive"
                  className="sr-only"
                  onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
                />
              </div>
            )}

            {file && (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[var(--color-brand)]/15 text-[var(--color-brand)]">
                    <FileBox size={18} />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate font-mono text-[12.5px] font-medium text-[var(--color-fg)]">{file.name}</div>
                    <div className="text-[11.5px] text-[var(--color-fg-muted)]">
                      {sizeMb.toFixed(1)} MB
                      {progress && uploadMut.isPending && (
                        <> · загружено {(progress.loaded / (1024 * 1024)).toFixed(1)} / {(progress.total / (1024 * 1024)).toFixed(1)} MB · {pct}%</>
                      )}
                    </div>
                  </div>
                </div>
                {!uploadMut.isPending && (
                  <button
                    onClick={() => setFile(null)}
                    className="grid h-8 w-8 place-items-center rounded-lg text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-fg)]"
                    title="Убрать файл"
                  >
                    <X size={14} />
                  </button>
                )}
                {uploadMut.isPending && (
                  <Spinner />
                )}
              </div>
            )}

            {file && uploadMut.isPending && (
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
                <div
                  className="h-full rounded-full bg-[var(--color-brand)] transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}

            {uploadMut.isError && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger-bg)] px-3 py-2 text-[12px] text-[var(--color-danger)]">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                <span>
                  {uploadMut.error instanceof ApiError
                    ? uploadMut.error.message
                    : "Ошибка загрузки"}
                </span>
              </div>
            )}
          </section>

          {/* Changelog */}
          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Что нового</h2>
            <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
              Передаётся вместе с APK. Будет показано на карточке приложения.
            </p>
            <textarea
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              placeholder={"- Исправлена ошибка X\n- Ускорена операция Y"}
              rows={5}
              className="mt-3 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
              disabled={uploadMut.isPending}
            />
          </section>
        </div>

        <aside className="space-y-3">
          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h3 className="text-[13px] font-semibold text-[var(--color-fg)]">Что произойдёт при публикации</h3>
            <ol className="mt-3 space-y-2 text-[12px]">
              <Numbered i={1}>APK проверяется (zip-magic, размер ≤ 200 МБ)</Numbered>
              <Numbered i={2}>Версия и поколение синтезируются (bump patch)</Numbered>
              <Numbered i={3}>Регистрируется как current, предыдущая → previous</Numbered>
              <Numbered i={4}>В фоне собираются дельты от последних версий</Numbered>
              <Numbered i={5}>Каталог переиздаётся, локалы подтягивают по своему расписанию</Numbered>
            </ol>
          </section>

          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h3 className="flex items-center gap-2 text-[13px] font-semibold text-[var(--color-fg)]">
              <ShieldCheck size={14} className="text-[var(--color-success)]" />
              Подпись
            </h3>
            <p className="mt-2 text-[11.5px] text-[var(--color-fg-muted)]">
              Реальный парсинг манифеста APK ещё не реализован — версия и поколение
              синтезируются как bump текущих. Подпись APK не валидируется.
            </p>
          </section>
        </aside>
      </div>
    </>
  );
}

function Numbered({ i, children }: { i: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--color-brand)] text-[10px] font-semibold text-white">
        {i}
      </span>
      <span className="text-[var(--color-fg)]">{children}</span>
    </li>
  );
}

function Spinner() {
  return (
    <span
      aria-label="Загрузка"
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-border-strong)] border-t-[var(--color-brand)]"
    />
  );
}
